import React, { memo, useCallback } from 'react';
import { useTableContext } from '../../context/TableContext';
import OpenInViewDropdown from '../../../shared/OpenInViewDropdown';

/**
 * Actions cell with buttons for task operations
 * Memoized to prevent re-renders when other cells change
 */
const ActionsCell = memo(({ row }) => {
  const { 
    campaignId,
    onOpenDetails,
    onAddSubtask, 
    onArchiveTask, 
    onUnarchiveTask,
    onOpenDeleteConfirmation,
    columnVisibility,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
  } = useTableContext();
  const task = row.original;
  const isMission = task.nodeType !== 'decision';
  
  // Check visibility settings (defaults to true if not set)
  const showOpenInView = columnVisibility?.openInViewButton !== false;
  const showDetailsButton = columnVisibility?.detailsButton !== false;
  const showArchiveButton = columnVisibility?.archiveButton !== false;
  const showDeleteButton = columnVisibility?.deleteButton !== false;

  // Memoize handlers
  const handleOpenDetails = useCallback(() => onOpenDetails?.(task.id), [onOpenDetails, task.id]);
  const handleAddSubtask = useCallback(() => onAddSubtask?.(task.id), [onAddSubtask, task.id]);
  const handleArchive = useCallback(() => onArchiveTask?.(task.id), [onArchiveTask, task.id]);
  const handleUnarchive = useCallback(() => onUnarchiveTask?.(task.id), [onUnarchiveTask, task.id]);
  const handleDelete = useCallback(() => onOpenDeleteConfirmation?.(task.id, task.name), [onOpenDeleteConfirmation, task.id, task.name]);

  return (
    <>
      {showOpenInView && (
        <OpenInViewDropdown
          taskId={task.id}
          campaignId={campaignId}
          currentView={currentView}
          customViews={customViews}
          activeCustomViewId={activeCustomViewId}
          variant="icon"
        />
      )}
      {showDetailsButton && (
        <button
          className="table-view__action-btn"
          onClick={handleOpenDetails}
          title="Open mission"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      )}
      {isMission && (
        <button
          className="table-view__action-btn"
          onClick={handleAddSubtask}
          title="Create Campaign Inside Mission"
          type="button"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}
      {showArchiveButton && (
        task.archived ? (
          <button
            className="table-view__action-btn table-view__action-btn--restore"
            onClick={handleUnarchive}
            title="Restore from archive"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 8 3 21 21 21 21 8" />
              <rect x="1" y="3" width="22" height="5" />
              <polyline points="10 12 14 16 10 20" />
              <line x1="14" y1="16" x2="6" y2="16" />
            </svg>
          </button>
        ) : (
          <button
            className="table-view__action-btn"
            onClick={handleArchive}
            title="Archive"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
          </button>
        )
      )}
      {showDeleteButton && (
        <button
          className="table-view__action-btn table-view__action-btn--danger"
          onClick={handleDelete}
          title="Delete"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevTask.id === nextTask.id && prevTask.archived === nextTask.archived && prevTask.name === nextTask.name
    && prevTask.nodeType === nextTask.nodeType;
});

export default ActionsCell;
